import { useState, useEffect, useCallback } from 'react';
import { notifications } from '@mantine/notifications';

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UseFetchListReturn<T> {
  data: T[];
  loading: boolean;
  totalCount: number;
  refetch: () => void;
}

/**
 * Manages loading state, data, and total count for paginated list pages.
 * Accepts a stable fetch function (typically wrapped with useCallback) that
 * returns a paginated response, and automatically re-fetches when that
 * function reference changes.
 *
 * Usage:
 *   const fetchCoaches = useCallback(() => coachesApi.list({ ... }), [deps]);
 *   const { data, loading, totalCount, refetch } = useFetchList(fetchCoaches);
 */
export function useFetchList<T>(
  fetchFn: () => Promise<PaginatedResponse<T>>,
): UseFetchListReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const execute = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchFn();
      setData(response.data);
      setTotalCount(response.pagination.total);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to load data',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => {
    execute();
  }, [execute]);

  return { data, loading, totalCount, refetch: execute };
}
